import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getNote } from "../lib/db";
import { setPrefetchedNote } from "../lib/notePrefetch";
import { useSelectionStore } from "../store/selection";
import { useUIStore } from "../store/ui";

/**
 * Keyboard navigation for note lists (arrow keys to browse, Enter/Space to focus editor).
 * Works globally — arrow keys are ignored when the editor or an input is focused.
 */
export function useNoteListNav(noteIds: string[]) {
  const { id: activeNoteId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSkipEditorFocus } = useUIStore();
  const hasSelection = useSelectionStore((s) => s.selectedIds.size > 0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      // Don't interfere with selection mode
      if (hasSelection) return;
      // Don't intercept when user is typing in an input, textarea, or the editor
      const el = document.activeElement;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el?.closest(".ProseMirror") ||
        el?.closest("[role='listbox']") ||
        el?.closest("[data-radix-popper-content-wrapper]")
      )
        return;
      if (noteIds.length === 0) return;
      e.preventDefault();

      const currentIndex = activeNoteId ? noteIds.indexOf(activeNoteId) : -1;

      let nextIndex: number;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex < noteIds.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : noteIds.length - 1;
      }

      setSkipEditorFocus(true);
      const nextId = noteIds[nextIndex];
      const nextNote = getNote(nextId);
      if (nextNote) setPrefetchedNote(nextNote);
      const qs = searchParams.toString();
      navigate(`/app/note/${nextId}${qs ? `?${qs}` : ""}`);

      // Focus the new note's button so Enter/Space activates it
      requestAnimationFrame(() => {
        const btn = document.querySelector<HTMLButtonElement>(
          `button[data-note-id="${nextId}"]`,
        );
        btn?.focus();
      });
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [
    noteIds,
    activeNoteId,
    navigate,
    searchParams,
    setSkipEditorFocus,
    hasSelection,
  ]);
}
